import React from "react";
import {
  Info,
  WarningCircle,
  EnvelopeSimple,
  Phone,
  Globe,
  MapPin,
} from "@phosphor-icons/react";
import { COMPANY } from "../../data/site";

// Shared building blocks for long-form policy pages rendered inside LegalLayout.

export function Section({ id, number, title, children }) {
  return (
    <section>
      <h2 id={id}>
        <span className="text-brandOrange mr-1.5">{number}.</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

export function Callout({ tone = "info", title, children }) {
  const warn = tone === "warn";
  const Icon = warn ? WarningCircle : Info;
  return (
    <div
      className={`my-5 flex gap-3 rounded-2xl border p-4 sm:p-5 text-sm leading-relaxed ${
        warn
          ? "bg-warnBg border-brandOrange/30 text-ink900"
          : "bg-infoBg border-brandBlue/25 text-ink900"
      }`}
    >
      <Icon
        size={20}
        weight="duotone"
        className={`mt-0.5 flex-shrink-0 ${warn ? "text-brandOrange" : "text-brandBlue"}`}
      />
      <div>
        {title && <div className="font-bold mb-1">{title}</div>}
        <div className="text-ink700">{children}</div>
      </div>
    </div>
  );
}

export function Pills({ items }) {
  return (
    <ul className="legal-plain flex flex-wrap gap-2 my-4">
      {items.map((item) => (
        <li
          key={item}
          className="rounded-full border border-line bg-surface px-3 py-1 text-xs font-semibold text-ink700"
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

export function DocumentInfo({ items }) {
  return (
    <dl className="grid sm:grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line">
      {items.map(([label, value]) => (
        <div key={label} className="bg-surfaceAlt px-4 py-3">
          <dt className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink500 mb-0.5">
            {label}
          </dt>
          <dd className="text-sm font-semibold text-ink900">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function StepTimeline({ steps }) {
  return (
    <ol className="legal-plain my-6 space-y-0">
      {steps.map((step, index) => (
        <li key={step.title} className="relative flex gap-4 pb-6 last:pb-0">
          {index < steps.length - 1 && (
            <span
              aria-hidden="true"
              className="absolute left-[17px] top-10 bottom-0 w-px bg-line"
            />
          )}
          <span className="relative z-10 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brandNavy text-sm font-bold text-white">
            {index + 1}
          </span>
          <div className="pt-1.5">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brandOrange">
              Step {index + 1}
            </div>
            <div className="font-bold text-ink900 mb-1">{step.title}</div>
            {step.body && <div className="text-sm text-ink700">{step.body}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

// Rows of { label, tone, description } where tone is a set of Tailwind bg/text classes.
export function BadgeTable({ rows }) {
  return (
    <div className="my-5 overflow-hidden rounded-2xl border border-line">
      {rows.map(({ label, tone, description }, index) => (
        <div
          key={label}
          className={`grid sm:grid-cols-[200px_minmax(0,1fr)] gap-2 sm:gap-4 px-4 py-4 ${
            index > 0 ? "border-t border-line" : ""
          }`}
        >
          <div>
            <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${tone}`}>
              {label}
            </span>
          </div>
          <div className="text-sm text-ink700">{description}</div>
        </div>
      ))}
    </div>
  );
}

export function CardGrid({ items }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3 my-5">
      {items.map(({ title, body }) => (
        <div key={title} className="rounded-2xl border border-line bg-surface p-5">
          <h3 className="!mt-0">{title}</h3>
          <div className="text-sm text-ink700">{body}</div>
        </div>
      ))}
    </div>
  );
}

export function SupportCard({ title = "Merchant Support" }) {
  return (
    <div className="mt-5 rounded-2xl border border-line bg-surface p-5">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brandOrange mb-3">
        {title}
      </div>
      <ul className="legal-plain grid sm:grid-cols-3 gap-3 text-sm">
        <li className="flex items-center gap-2">
          <EnvelopeSimple size={18} weight="duotone" className="text-brandBlue flex-shrink-0" />
          <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
        </li>
        <li className="flex items-center gap-2">
          <Phone size={18} weight="duotone" className="text-brandBlue flex-shrink-0" />
          <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}>{COMPANY.phone}</a>
        </li>
        <li className="flex items-center gap-2">
          <Globe size={18} weight="duotone" className="text-brandBlue flex-shrink-0" />
          <a href="/contact">jayespay.com/contact</a>
        </li>
      </ul>
    </div>
  );
}

export function CompanyCard() {
  return (
    <div className="rounded-2xl bg-ink900 text-surface p-6 sm:p-7">
      <div className="font-heading font-extrabold text-xl text-white">{COMPANY.name}</div>
      <div className="text-sm text-surface/75 mb-5">A Product of {COMPANY.legalName}</div>
      <ul className="legal-plain space-y-3 text-sm text-surface/85">
        <li>
          <span className="text-surface/55">CIN:</span>{" "}
          <span className="font-semibold text-white">{COMPANY.cin}</span>
        </li>
        <li className="flex items-start gap-2.5">
          <MapPin size={18} weight="duotone" className="mt-0.5 text-sky-200 flex-shrink-0" />
          <span>
            <span className="block text-surface/55 text-xs">
              Registered/Published Business Address
            </span>
            {COMPANY.address}
          </span>
        </li>
        <li className="flex items-center gap-2.5">
          <EnvelopeSimple size={18} weight="duotone" className="text-sky-200 flex-shrink-0" />
          <a href={`mailto:${COMPANY.email}`} className="!text-white hover:!text-brandOrange">
            {COMPANY.email}
          </a>
        </li>
        <li className="flex items-center gap-2.5">
          <Phone size={18} weight="duotone" className="text-sky-200 flex-shrink-0" />
          <a
            href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
            className="!text-white hover:!text-brandOrange"
          >
            {COMPANY.phone}
          </a>
        </li>
        <li className="flex items-center gap-2.5">
          <Globe size={18} weight="duotone" className="text-sky-200 flex-shrink-0" />
          <a href="/" className="!text-white hover:!text-brandOrange">
            jayespay.com
          </a>
        </li>
      </ul>
    </div>
  );
}
